import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
// import zxcvbn from 'zxcvbn'; // Commented out - zxcvbn removed to reduce bundle size
import {
  defineMessage,
  defineMessages,
  MessageDescriptor,
} from '@formatjs/intl';
import TokenizedApi from '../TokenizedApi';
import Service, { minutes, seconds } from './Service';
import {
  TokenizedApiError,
  TokenizedAuthError,
  TokenizedResponseInvalidError,
  TokenizedValidationError,
  serializeError,
  TSerializedError,
  TErrorInfo,
  TokenizedPermissionRestrictionError,
} from '../utils/errors';
import {
  delayForSeconds,
  makeDebouncedAsyncValidator,
  urlPath,
} from '../utils/utils';
import { hmac } from '../utils/crypto';
import { handleDomains, TBackend } from '../utils/backends';
import {
  restApiDevice,
  restApiHandshake,
  restApiUser,
  restApiHandleAvailability,
  restApiAccountRecoveryDetails,
  restApiRenewedToken,
  restApiHandshakeRequiredNoActivity,
  THandshake,
  TUser,
  restApiPermissionRestrictionMessage,
  TPermissionRestrictionMessage,
} from './TokenizedApiModelEntities';
import * as schema from '../utils/schema';
import type { TReduxState, TThunkAction } from './ReduxState';
import { handshakeConfirmationCode, MFARequest } from './MFA';
import { AxiosResponse } from 'axios';
import {
  computeCodeChallenge,
  getCodeVerifier,
  makeCodeVerifier,
} from '../utils/oauth';
import isAuthenticatorVersionSupported from '../utils/isAuthenticatorVersionSupported';

// For debugging – forces a mobile version upgrade request on log-in
const FORCE_AUTHENTICATOR_UNSUPPORTED = false;

/**
 * Identifies the purpose (scope) of a verification code:
 *
 * - `"account"`: Confirms a new account
 * - `"password"`: Confirms a new login password
 * - `"authenticator"`: Confirms pairing with an authentication device
 * - `"phone"`: Confirms a new phone number
 * - `"email"`: Confirms a new email address
 *
 * @category Logging in and Security
 */
export type TVerificationCodeScope = (typeof VERIFY_SCOPES)[number];
/** @category Logging in and Security */
export const VERIFY_SCOPES = [
  'account',
  'password',
  'authenticator',
  'phone',
  'email',
] as const;

/** @category Logging in and Security */
export type TAccountIdentifierType = 'identifier' | 'handle' | 'email';

/** @category Logging in and Security */
export interface TAccountIdentifier {
  /** @internal */
  identifier?: string;
  /**
   * Joined with the {@link Account.getUserHandlePostfix | user handle postfix}
   * to identify the account. So for example specifying `handle:
   * 'hankrearden'` will log in as `hankrearden@tkz.id` on the
   * production back end. Specify only one of `handle`, or `email`.
   */
  handle?: string;
  /**
   * Identifies the account to log into using
   * the email address registered to the account. Specify only one of
   * `handle`, or `email`.
   */
  email?: string;
}

/** @category Logging in and Security */
export interface TParsedAccountIdentifier {
  errorType?:
    | 'invalid'
    | 'multipleAts'
    | 'incomplete'
    | 'handleDomainNotConfigured'
    | 'handleDomainUnsupported';
  error?: Error;

  identifierType?: TAccountIdentifierType;
  /** The full identifier in normalized form */
  identifier?: string;
  /**
   * For a handle identifier, The unique username part. For example
   * `hankrearden@tkz.id` has `handle: 'hankrearden'`.
   */
  handle?: string;
  /**
   * For a handle identifier, The domain part. For example
   * `hankrearden@tkz.id` has `handleDomain: 'tkz.id'`.
   */
  handleDomain?: string;
  /**
   * For a handle identifier, The Tokenized backend. for example
   * `hankrearden@tkz.id` has `handleBackend: 'production'`.
   */
  handleBackend?: TBackend;
}

type TLogInStatus =
  | 'loading'
  | 'loggedOut'
  | 'needsToken'
  | 'checkingPersonalProfile'
  | 'needsPersonalProfile'
  | 'checkingDevicePairing'
  | 'needsDevicePairing'
  | 'waitingForMfa'
  | 'checkingAuthenticatorVersion'
  | 'needsAuthenticatorUpdate'
  | 'checkingRootKey'
  | 'needsRootKey'
  | 'checkingBackupStatus'
  | 'needsBackupHealthCheck'
  | 'logInComplete'
  | 'loggedIn';
type TLogInStage =
  | 'start'
  | 'requestToken'
  | 'handleCreation'
  | 'devicePairing'
  | 'authenticatorUpdate'
  | 'rootKeyCreation'
  | 'backupHealthCheck'
  | 'mfa';
const AUTH_STATUS_TO_NEXT_LOGIN_STEP: Record<
  TLogInStatus,
  TLogInStage | undefined
> = {
  loading: undefined,
  loggedOut: 'start',
  needsToken: 'requestToken',
  checkingPersonalProfile: 'handleCreation',
  needsPersonalProfile: 'handleCreation',
  checkingDevicePairing: 'devicePairing',
  needsDevicePairing: 'devicePairing',
  waitingForMfa: 'mfa',
  checkingAuthenticatorVersion: 'authenticatorUpdate',
  needsAuthenticatorUpdate: 'authenticatorUpdate',
  checkingRootKey: 'rootKeyCreation',
  needsRootKey: 'rootKeyCreation',
  checkingBackupStatus: 'backupHealthCheck',
  needsBackupHealthCheck: 'backupHealthCheck',
  logInComplete: undefined,
  loggedIn: undefined,
} as const;

/** @category Logging in and Security */
export type TLogInChecking =
  | 'credentials'
  | 'account'
  | 'authenticator'
  | 'mfa'
  | 'versions'
  | 'rootKey'
  | 'backups';
/** @category Logging in and Security */
export interface TLogInProgress {
  percent: number;
  checking?: TLogInChecking;
}
const LOGIN_PERCENT_STEP = 100 / 14;
const AUTH_STATUS_TO_LOGIN_PROGRESS: Record<TLogInStatus, TLogInProgress> = {
  loading: {
    percent: 0 * LOGIN_PERCENT_STEP,
  },
  loggedOut: {
    percent: 0 * LOGIN_PERCENT_STEP,
  },
  needsToken: {
    percent: 1 * LOGIN_PERCENT_STEP,
    checking: 'credentials',
  },
  checkingPersonalProfile: {
    percent: 2 * LOGIN_PERCENT_STEP,
    checking: 'account',
  },
  needsPersonalProfile: {
    percent: 3 * LOGIN_PERCENT_STEP,
    checking: 'account',
  },
  checkingDevicePairing: {
    percent: 4 * LOGIN_PERCENT_STEP,
    checking: 'authenticator',
  },
  needsDevicePairing: {
    percent: 5 * LOGIN_PERCENT_STEP,
    checking: 'authenticator',
  },
  waitingForMfa: {
    percent: 6 * LOGIN_PERCENT_STEP,
    checking: 'mfa',
  },
  checkingAuthenticatorVersion: {
    percent: 7 * LOGIN_PERCENT_STEP,
    checking: 'versions',
  },
  needsAuthenticatorUpdate: {
    percent: 8 * LOGIN_PERCENT_STEP,
    checking: 'versions',
  },
  checkingRootKey: {
    percent: 8 * LOGIN_PERCENT_STEP,
    checking: 'rootKey',
  },
  needsRootKey: {
    percent: 10 * LOGIN_PERCENT_STEP,
    checking: 'rootKey',
  },
  checkingBackupStatus: {
    percent: 11 * LOGIN_PERCENT_STEP,
    checking: 'backups',
  },
  needsBackupHealthCheck: {
    percent: 12 * LOGIN_PERCENT_STEP,
    checking: 'backups',
  },
  logInComplete: {
    percent: 13 * LOGIN_PERCENT_STEP,
  },
  loggedIn: {
    percent: 14 * LOGIN_PERCENT_STEP,
  },
};

/**
 * @category Logging in and Security
 * @internal
 */
export const selectQueryEnabledStatus = (state: TReduxState) =>
  state?.account?.allowQueries;
/** @category Logging in and Security */
export const selectIsLoading = (state: TReduxState) =>
  selectAuthStatus(state) === 'loading';
/** @category Logging in and Security */
export const selectIsLoggingIn = (state: TReduxState) =>
  !!selectLogInProcessId(state);
/** @category Logging in and Security */
export const selectLogInProgress = (state: TReduxState) =>
  AUTH_STATUS_TO_LOGIN_PROGRESS[selectAuthStatus(state)];
/** @category Logging in and Security */
export const selectIsLoggedOut = (state: TReduxState) =>
  selectAuthStatus(state) === 'loggedOut';
/** @category Logging in and Security */
export const selectLogInIdentifier = (state: TReduxState) =>
  state?.account?.logInIdentifier;
/** @category Logging in and Security */
export const selectLogInIdentifierType = (state: TReduxState) =>
  state?.account?.logInIdentifierType;
/** @category Logging in and Security */
export const selectVerificationEmailAddress = (state: TReduxState) =>
  selectLogInIdentifierType(state) === 'email'
    ? selectLogInIdentifier(state)
    : null;
/** @category Logging in and Security */
export const selectLogInNeedsMfa = (state: TReduxState) =>
  selectAuthStatus(state) === 'waitingForMfa';
/** @category Logging in and Security */
export const selectLogInNeedsPersonalProfile = (state: TReduxState) =>
  selectAuthStatus(state) === 'needsPersonalProfile';
/** @category Logging in and Security */
export const selectLogInNeedsAuthenticatorUpdate = (state: TReduxState) =>
  selectAuthStatus(state) === 'needsAuthenticatorUpdate';
/** @category Logging in and Security */
export const selectLogInNeedsBackupHealthCheck = (state: TReduxState) =>
  selectAuthStatus(state) === 'needsBackupHealthCheck';
/** @category Logging in and Security */
export const selectLogInConfirmationCode = (state: TReduxState) =>
  state.account.confirmationCode;
/** @category Logging in and Security */
export const selectPermissionRestrictionsAtLogIn = (state: TReduxState) =>
  state.account.permissionRestrictionsAtLogIn;
const LOGIN_INVALID_STATUSES: TLogInStatus[] = [
  'loading',
  'loggedOut',
  'needsToken',
];
/** @category Logging in and Security */
export const selectLogInIsValid = (state: TReduxState) =>
  !LOGIN_INVALID_STATUSES.includes(selectAuthStatus(state));
const LOGIN_COMPLETE_STATUSES: TLogInStatus[] = ['logInComplete', 'loggedIn'];
/** @category Logging in and Security */
export const selectLogInIsComplete = (state: TAccountStateFromRoot) =>
  LOGIN_COMPLETE_STATUSES.includes(selectAuthStatus(state));
/** @category Logging in and Security */
export const selectIsLoggedIn = (state: TReduxState) =>
  selectAuthStatus(state) === 'loggedIn';
/** @category Logging in and Security */
export const selectLogInError = (state: TReduxState) =>
  state?.account?.logInError;

/** @category Logging in and Security */
export const selectIsWaitingForDevicePairing = (state: TReduxState) =>
  !!state?.account?.devicePairing;
/** @category Logging in and Security */
export const selectPlatformApiKey = (state: TReduxState) =>
  state?.account?.platformApiKey;
/** @category Logging in and Security */
export const selectTokenizedBackend = (state: TReduxState) =>
  state?.account?.tokenizedBackend;
/** @category Logging in and Security */
export const selectLogInPairingCodePayload = (state: TReduxState) => {
  if (!selectIsWaitingForDevicePairing(state)) {
    return null;
  }
  const authToken = selectPreAuthToken(state);
  if (!authToken) {
    return null;
  }
  return JSON.stringify({
    env: selectTokenizedBackend(state),
    sign: 1,
    authToken,
    apiKey: selectPlatformApiKey(state),
  });
};

/** @category Logging in and Security */
export interface TLogInChecklist {
  handleCreated?: boolean;
  devicePaired?: boolean;
  authenticatorVersionSupported?: boolean;
  rootKeyCreated?: boolean;
  backupsHealthy?: boolean;
}

export class TokenizedLogInCredentialsError extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Incorrect credentials', {
      matchesWithFieldName: 'password',
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage: 'Incorrect credentials',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.login.credentials',
    });
  }
}

export class TokenizedLogInDeniedError extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Log-in cancelled by mobile app', {
      matchesWithFieldName: 'password',
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage: 'Log-in cancelled by mobile app',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.login.cancelled.mobile',
    });
  }
}

export class TokenizedClockSyncError extends TokenizedAuthError {
  /** @internal */
  constructor() {
    super('Device clock not in sync with server', {
      isSafeToIgnoreForBugTracking: false,
    });
    this.formattedMessage = defineMessage({
      defaultMessage:
        'Device clock not in sync with server - see https://forum.tokenized.com/t/checking-clock-synchronisation/365 for more information',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.login.clock.sync',
    });
  }
}

export class TokenizedLogInCancelledError extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Log-in cancelled', {
      matchesWithFieldName: 'password',
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage: 'Log-in cancelled',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.login.cancelled',
    });
  }
}

export class TokenizedLogInInvalidAccount extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('No user account was found', {
      matchesWithFieldName: 'identifier',
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage: 'No user account was found',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.login.account.invalid',
    });
  }
}

export class TokenizedAccountVerificationInvalidError extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Invalid or expired code', {
      matchesWithFieldName: 'emailVerificationCode',
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage: 'Invalid or expired code',
      description: 'Failed log-in error message',
      id: 'TokenizedSDK:error.account.verify.invalid',
    });
  }
}

export class TokenizedSignUpEmailRestrictedError extends TokenizedAuthError {
  /** @internal */
  constructor(additionalInfo?: TErrorInfo) {
    super('Sign up is currently restricted', {
      matchesWithFieldName: 'email',
      isSafeToIgnoreForBugTracking: false,
      ...additionalInfo,
    });
    this.formattedMessage = defineMessage({
      defaultMessage:
        'Sign up is currently restricted. Please contact support.',
      description: 'Failed sign-up due to restricted email',
      id: 'TokenizedSDK:error.account.email.restricted',
    });
  }
}

/** @category Logging in and Security */
export interface TAccountState {
  confirmationCode?: string;
  isLoading: boolean;
  tokenIsFullyAuthorized: boolean | undefined;
  tokenExpiryTimestamp: number | undefined;
  token: string | null;
  tokenFromDeviceId: string | undefined;
  activeLogInProcessId: string | null;
  logInIdentifier: string | null;
  logInIdentifierType: TAccountIdentifierType | null;
  logInPassword: string | null;
  checklist: TLogInChecklist;
  logInError: TSerializedError | null;
  resetPasswordIdentifier: string | null;
  devicePairing: boolean | null;
  devicePairingDeviceCountSnapshot: number | undefined;
  platformApiKey: string | null | undefined;
  tokenizedBackend: TBackend | undefined;
  allowQueries: {
    validDuringMfa: boolean;
    all: boolean;
  };
  permissionRestrictionsAtLogIn: TPermissionRestrictionMessage[] | undefined;
}
interface TAccountStateFromRoot {
  account: TAccountState;
}

const initialState: TAccountState = {
  isLoading: true,
  tokenIsFullyAuthorized: undefined,
  tokenExpiryTimestamp: undefined,
  token: null,
  tokenFromDeviceId: undefined,
  activeLogInProcessId: null,
  logInIdentifier: null,
  logInIdentifierType: null,
  logInPassword: null,
  checklist: {
    handleCreated: undefined,
    devicePaired: undefined,
    authenticatorVersionSupported: undefined,
    rootKeyCreated: undefined,
    backupsHealthy: undefined,
  },
  logInError: null,
  resetPasswordIdentifier: null,
  devicePairing: false,
  devicePairingDeviceCountSnapshot: undefined,
  platformApiKey: undefined,
  tokenizedBackend: undefined,
  allowQueries: {
    validDuringMfa: false,
    all: false,
  },
  permissionRestrictionsAtLogIn: undefined,
};

function clearStateOnLogOut(state: TAccountState) {
  state.tokenIsFullyAuthorized = undefined;
  state.tokenExpiryTimestamp = undefined;
  state.token = null;
  state.tokenFromDeviceId = undefined;
  state.activeLogInProcessId = null;
  state.logInIdentifier = null;
  state.logInIdentifierType = null;
  state.logInPassword = null;
  state.checklist = {
    devicePaired: undefined,
    handleCreated: undefined,
    authenticatorVersionSupported: undefined,
    rootKeyCreated: undefined,
    backupsHealthy: undefined,
  };
  state.resetPasswordIdentifier = null;
  state.devicePairing = false;
  state.devicePairingDeviceCountSnapshot = undefined;
  state.allowQueries = {
    validDuringMfa: false,
    all: false,
  };
  state.permissionRestrictionsAtLogIn = undefined;
}

/** @category Logging in and Security */
export interface TDecodedToken {
  token: string | null;
  tokenIsFullyAuthorized: boolean;
  tokenExpiryTimestamp: number | undefined;
}

function decodeAndCheckToken(token: unknown): TDecodedToken {
  if (token && typeof token === 'string') {
    let decodedToken;
    try {
      decodedToken = jwtDecode(token) as Record<string, unknown>;
    } catch (error) {
      decodedToken = null;
    }
    if (typeof decodedToken?.exp === 'number') {
      const tokenExpiryTimestamp = new Date(decodedToken.exp * 1000).getTime();
      // To test token expiry (makes tokens refresh every 30s):
      // const tokenExpiryTimestamp = Date.now() + (60 * 5 + 30) * 1000;
      if (tokenExpiryTimestamp > Date.now()) {
        // console.log(
        //   `New token expires in ${
        //     (tokenExpiryTimestamp - Date.now()) / 1000 / 60
        //   } mins`,
        // );
        return {
          token,
          tokenIsFullyAuthorized:
            decodedToken.aud === 'MobileAPI' || !!decodedToken.mfa,
          tokenExpiryTimestamp,
        };
      }
    }
  }
  return {
    token: null,
    tokenIsFullyAuthorized: false,
    tokenExpiryTimestamp: undefined,
  };
}

type NewTokenPayloadAction = PayloadAction<
  TDecodedToken & {
    allowAllQueries?: boolean;
    confirmationCode?: string;
    permissionRestrictionsAtLogIn?: TPermissionRestrictionMessage[];
    fromDeviceId?: string;
  }
>;

const newTokenReducer = (
  state: TAccountState,
  action: NewTokenPayloadAction,
) => {
  state.confirmationCode = action.payload.confirmationCode;
  state.token = action.payload.token;
  state.tokenIsFullyAuthorized = action.payload.tokenIsFullyAuthorized;
  state.tokenExpiryTimestamp = action.payload.tokenExpiryTimestamp;
  if (action.payload.fromDeviceId) {
    state.tokenFromDeviceId = action.payload.fromDeviceId;
  }
  state.allowQueries = {
    validDuringMfa: !!action.payload.allowAllQueries || !!action.payload.token,
    all:
      !!action.payload.allowAllQueries || action.payload.tokenIsFullyAuthorized,
  };
  if (action.payload.permissionRestrictionsAtLogIn) {
    state.permissionRestrictionsAtLogIn =
      action.payload.permissionRestrictionsAtLogIn;
  }
};

/**
 * @category Logging in and Security
 * @internal
 */
export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    resolvePlatformApiKey: (state, action: PayloadAction<string | null>) => {
      state.platformApiKey = action.payload;
    },

    setTokenizedBackend: (state, action: PayloadAction<TBackend>) => {
      state.tokenizedBackend = action.payload;
    },

    restoreSession: (state, action: NewTokenPayloadAction) =>
      newTokenReducer(state, action),

    loadingEnd: (state) => {
      state.isLoading = false;
    },

    logInProcessStart: (
      state,
      action: PayloadAction<{
        logInProcessId: string;
        identifier: string;
        identifierType: TAccountIdentifierType;
        logInPassword: string;
      }>,
    ) => {
      state.activeLogInProcessId = action.payload?.logInProcessId;
      state.logInIdentifier = action.payload?.identifier;
      state.logInIdentifierType = action.payload?.identifierType;
      state.logInPassword = action.payload?.logInPassword;
      state.logInError = null;
      state.resetPasswordIdentifier = null;
    },

    clearLogInError: (state) => {
      state.logInError = null;
    },

    clearLogInPassword: (state) => {
      state.logInPassword = null;
    },

    requestAuthTokenResponse: (state, action: NewTokenPayloadAction) =>
      newTokenReducer(state, action),

    retrieveLogInHandshakeTokenResponse: (
      state,
      action: NewTokenPayloadAction,
    ) => newTokenReducer(state, action),

    renewTokenResponse: (state, action: NewTokenPayloadAction) =>
      newTokenReducer(state, action),

    logInProcessUpdateChecklist: (
      state,
      action: PayloadAction<TLogInChecklist>,
    ) => {
      Object.assign(state.checklist, action.payload || {});
    },

    logInProcessError: {
      reducer: (
        state,
        action: PayloadAction<null, string, { errorObject: Error }, boolean>,
      ) => {
        state.logInError = serializeError(action.meta?.errorObject);
      },
      prepare: (error: unknown) => ({
        payload: null,
        error: true,
        meta: {
          errorObject: error instanceof Error ? error : new Error(),
        },
      }),
    },

    logInProcessEnd: (state, action: PayloadAction<string>) => {
      if (state.activeLogInProcessId === action.payload) {
        state.activeLogInProcessId = null;
        if (!selectLogInIsComplete({ account: state })) {
          clearStateOnLogOut(state);
        }
      }
    },

    startResetPassword: (
      state,
      action: PayloadAction<{
        identifier: string;
      }>,
    ) => {
      state.resetPasswordIdentifier = action.payload?.identifier;
    },

    cancelResetPassword: (state) => {
      state.resetPasswordIdentifier = null;
    },

    startDevicePairing: (
      state,
      action: PayloadAction<{
        currentDeviceCount?: number;
      }>,
    ) => {
      state.devicePairing = true;
      state.devicePairingDeviceCountSnapshot =
        action.payload?.currentDeviceCount;
    },

    stopDevicePairing: (state) => {
      state.devicePairing = false;
      state.devicePairingDeviceCountSnapshot = undefined;
    },

    logOut: (state) => {
      clearStateOnLogOut(state);
    },
  },
});

/**
 * @category Logging in and Security
 * @internal
 */
export const resolvePlatformApiKeyAction =
  accountSlice.actions.resolvePlatformApiKey;
/**
 * @category Logging in and Security
 * @internal
 */
export const setTokenizedBackendAction =
  accountSlice.actions.setTokenizedBackend;

// Internal selectors
const selectLogInPassword = (state: TReduxState) =>
  state?.account?.logInPassword;
const selectAuthStatus = (state: TAccountStateFromRoot): TLogInStatus => {
  if (state?.account?.isLoading) {
    return 'loading';
  }
  const tokenValid =
    state?.account?.token &&
    state?.account?.tokenExpiryTimestamp &&
    state?.account?.tokenExpiryTimestamp > Date.now();
  if (selectLogInProcessId(state)) {
    if (!tokenValid) {
      return 'needsToken';
    }
    if (!state?.account?.checklist?.handleCreated) {
      return state?.account?.checklist?.handleCreated === false
        ? 'needsPersonalProfile'
        : 'checkingPersonalProfile';
    }
    if (!state?.account?.checklist?.devicePaired) {
      return state?.account?.checklist?.devicePaired === false
        ? 'needsDevicePairing'
        : 'checkingDevicePairing';
    }
    if (!state?.account?.tokenIsFullyAuthorized) {
      return 'waitingForMfa';
    }
    if (!state?.account?.checklist?.authenticatorVersionSupported) {
      return state?.account?.checklist?.authenticatorVersionSupported === false
        ? 'needsAuthenticatorUpdate'
        : 'checkingAuthenticatorVersion';
    }
    if (!state?.account?.checklist?.rootKeyCreated) {
      return state?.account?.checklist?.rootKeyCreated === false
        ? 'needsRootKey'
        : 'checkingRootKey';
    }
    if (!state?.account?.checklist?.backupsHealthy) {
      return state?.account?.checklist?.backupsHealthy === false
        ? 'needsBackupHealthCheck'
        : 'checkingBackupStatus';
    }
    return 'logInComplete';
  } else if (tokenValid && state?.account?.tokenIsFullyAuthorized) {
    return 'loggedIn';
  }
  return 'loggedOut';
};
/**
 * @category Logging in and Security
 * @internal
 */
export const selectPreAuthToken = (state: TReduxState) =>
  state?.account?.tokenExpiryTimestamp &&
  state?.account?.tokenExpiryTimestamp > Date.now()
    ? state?.account?.token
    : null;
/**
 * @category Logging in and Security
 * @internal
 */
export const selectAuthToken = (state: TReduxState) =>
  selectPreAuthToken(state) && state?.account?.tokenIsFullyAuthorized
    ? state?.account?.token
    : null;
export const selectDeviceIdThatApprovedLogin = (state: TReduxState) =>
  state?.account?.tokenFromDeviceId;
export const selectTokenNeedsRefresh = (state: TReduxState) =>
  !!state?.account?.tokenExpiryTimestamp &&
  Date.now() >= state.account.tokenExpiryTimestamp - AUTH_TOKEN_REFRESH_WINDOW;
const selectLogInProcessId = (state: TAccountStateFromRoot) =>
  state?.account?.activeLogInProcessId;
const selectIsLogInProcessActive = (
  state: TReduxState,
  logInProcessId: string,
) => selectIsLoggingIn(state) && selectLogInProcessId(state) === logInProcessId;
const selectLogInProcessNextStep = (state: TReduxState) =>
  AUTH_STATUS_TO_NEXT_LOGIN_STEP[selectAuthStatus(state)];
const LOGIN_POLLED_STEPS = [
  'handleCreation',
  'devicePairing',
  'mfa',
  'authenticatorUpdate',
  'rootKeyCreation',
  'backupHealthCheck',
];
/**
 * @category Logging in and Security
 * @internal
 */
export const selectLogInNeedsPolling = (state: TReduxState) => {
  const nextStep = selectLogInProcessNextStep(state);
  return !!nextStep && LOGIN_POLLED_STEPS.includes(nextStep);
};
export const selectHandleCreated = (state: TReduxState) => {
  return state.account.checklist.handleCreated;
};
const selectResetPasswordIdentifier = (state: TReduxState) =>
  state?.account?.resetPasswordIdentifier;
const selectPairingPreviousDeviceCount = (state: TReduxState) =>
  state?.account?.devicePairingDeviceCountSnapshot;

const TOKEN_SCOPES = [
  'login',
  'password_update',
  'withdrawal',
  'post_contracts',
  'phone',
  'email',
] as const;
type TokenScopeType = (typeof TOKEN_SCOPES)[number];

const LOGIN_PASSWORD_HASH = 'tokenized-login-password-key';
// Try to refresh the token when it expires in less than 5 minutes
const AUTH_TOKEN_REFRESH_WINDOW = 5 * 60 * 1000;
const CHECK_TOKEN_REFRESH_INTERVAL = 10 * 1000;

const AUTH_TOKEN_PERSIST_KEY = 'authToken';
const HANDSHAKE_POLL_INTERVAL_SECS = 3;

/** @category Logging in and Security */
export interface TDisplayUser extends TUser {
  formattedName?: string;
}

/**
 * Manages the authentication token for the session and handles account
 * creation, device pairing, sign in, and recovery
 *
 * @category SDK Modules
 */
class Account extends Service {
  /** @internal */
  userHandleDomain: string;
  /** @internal */
  onLogOut?: () => void;
  /** @internal */
  accountCreationSupported: boolean;

  // Enforced by desktop app on startup. Note that the API can also impose a
  // per-account min authenticator version by setting a permission like so:
  //   {
  //     topic: 'ensureMinAuthenticatorVersion',
  //     code: '1.6.17',
  //   }
  static MIN_SUPPORTED_AUTHENTICATOR_VERSION = '1.6.17';

  /** @internal */
  constructor(tokenizedApi: TokenizedApi) {
    super(tokenizedApi);

    const config = tokenizedApi.config;

    this.accountCreationSupported = !!config.accountCreationSupported;
    if (!config.userHandleDomain) {
      if (
        !config.tokenizedBackend ||
        typeof config.tokenizedBackend !== 'string' ||
        !handleDomains[config.tokenizedBackend]
      ) {
        throw new TypeError(
          `Invalid config: tokenizedBackend must be one of ${Object.keys(
            handleDomains,
          )
            .map((backend) => `'${backend}'`)
            .join()}`,
        );
      }
      this.userHandleDomain = handleDomains[config.tokenizedBackend];
    } else {
      if (typeof config.userHandleDomain !== 'string') {
        throw new TypeError(
          'Invalid config: userHandleDomain must be a string or null',
        );
      }
      this.userHandleDomain = config.userHandleDomain;
    }
    if (config.onLogOut) {
      if (typeof config.onLogOut !== 'function') {
        throw new TypeError(
          'Invalid config: onLogOut must be a function or null',
        );
      }
      this.onLogOut = config.onLogOut;
    }

    if (config.enableAutomaticTokenRefresh && !config.mobileAuthToken) {
      setInterval(
        () => this.ensureTokenRefresh(),
        CHECK_TOKEN_REFRESH_INTERVAL,
      );
    }
  }

  /** @internal */
  getEnableQueriesValidDuringMfa() {
    return !!selectQueryEnabledStatus(this.tokenizedApi.getReduxState())
      ?.validDuringMfa;
  }

  /** @internal */
  getEnableAllQueries() {
    return !!selectQueryEnabledStatus(this.tokenizedApi.getReduxState())?.all;
  }

  /** @internal */
  requestAuthTokenAction(
    clientId: string,
    sourceName: string,
    username: string,
    password: string,
  ): TThunkAction<Promise<TDecodedToken>> {
    return async (dispatch) => {
      if (!clientId || typeof clientId !== 'string') {
        throw new TypeError(
          'Request auth token arguments: clientId must be a string',
        );
      }
      if (!sourceName || typeof sourceName !== 'string') {
        throw new TypeError(
          'Request auth token arguments: sourceName must be a string',
        );
      }
      if (!username || typeof username !== 'string') {
        throw new TypeError(
          'Request auth token arguments: username must be a string',
        );
      }
      if (!password || typeof password !== 'string') {
        throw new TypeError(
          'Request auth token arguments: password must be a string',
        );
      }
      let response;
      try {
        response = await this.tokenizedApi.endpoints.get(
          urlPath('auth', 'token'),
          {
            params: {
              client_id: clientId,
              source_name: sourceName,
            },
            auth: {
              username,
              password,
            },
          },
          null, // uses basic auth, not JWT
        );
      } catch (error) {
        if (error instanceof TokenizedAuthError) {
          throw new TokenizedLogInCredentialsError({
            fromError: error,
          }).withFormatting(this);
        }
        throw error;
      }

      this.checkClockSkew(response);

      const checkedToken = decodeAndCheckToken(response?.data?.data?.token);

      const handshakeId = response?.data?.data.handshake_id;
      const confirmationCode =
        typeof handshakeId == 'string'
          ? await handshakeConfirmationCode(handshakeId)
          : undefined;

      let permissionRestrictionsAtLogIn:
        | TPermissionRestrictionMessage[]
        | undefined;
      if (response?.data?.data?.messages) {
        try {
          permissionRestrictionsAtLogIn =
            schema.normalizeResponse(
              'Permission restrictions from auth',
              response?.data?.data?.messages,
              schema.arrayOf(restApiPermissionRestrictionMessage),
            ).normalizedValue || undefined;
        } catch (error) {
          // ignore
        }
      }

      dispatch(
        accountSlice.actions.requestAuthTokenResponse({
          ...checkedToken,
          confirmationCode,
          permissionRestrictionsAtLogIn,
        }),
      );

      if (!checkedToken.token) {
        throw new TokenizedResponseInvalidError(
          'Request auth token: response invalid',
        ).withFormatting(this);
      }
      return checkedToken;
    };
  }

  /** @internal */
  supplyToken(authToken: string) {
    this.tokenizedApi.state.dispatch(
      accountSlice.actions.requestAuthTokenResponse(
        decodeAndCheckToken(authToken),
      ),
    );
  }

  /** @internal */
  persistAuthTokenForSessionRestore() {
    if (!this.tokenizedApi.config.enableSessionPersistence) {
      return;
    }

    const token = selectAuthToken(this.tokenizedApi.getReduxState());
    if (token) {
      this.tokenizedApi.persistentState.setItem(AUTH_TOKEN_PERSIST_KEY, token);
    }
  }

  /** @internal */
  handleAvailabilityQuery = this.makeQuery(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (query, handle?: string, _entityId?: string, _domainId?: string) => ({
      ...query,
      // Only really want to cache this while a dialog is being
      // interacted with, so expires quickly
      staleTime: minutes(1),
      // Note: handle availability queries can be sent with no auth
      // token at all using QueryClient.fetchQuery, but subscription
      // with an observer is not allowed on startup
      enabled: this.getEnableQueriesValidDuringMfa() && !!handle,
    }),
    async (context, handle?: string, entityId?: string, domainId?: string) => {
      if (!handle) {
        return false;
      }
      const response = await this.tokenizedApi.endpoints.post(
        urlPath('handles', 'check'),
        {
          username: handle,
          entity_id: entityId || null,
          domain_id: domainId || null,
        },
        undefined,
        null, // no auth token required
      );
      const normalizedResponse = schema.normalizeResponse(
        'Query handle availability',
        response?.data,
        restApiHandleAvailability,
      );
      return !!normalizedResponse.normalizedValue?.available;
    },
  );

  /**
   * Use this in a new account dialog, to show the user as they type whether
   * their paymail unique ID (handle) is already in use. You can call the
   * returned async function as often as you like (on every keystroke), and it
   * won’t send more than one query to the back end every 500ms. Each time a
   * query is sent, the result will become the resolved value of all unresolved
   * promises previously returned.
   *
   * Note that each validator function returned by this method maintains its own
   * separate timer for debouncing, so if you call
   * `makeDebouncedHandleAvailabilityChecker` in a React render function, you
   * must memoize the result with `useMemo` or similar, to maintain the same
   * validator for the lifetime of your component.
   *
   * @returns The async validation function, which takes one
   *   argument, the handle string to check, and returns a promise that resolves
   *   to `true` if the *most recent* handle that was checked is available
   *   (because the queries are debounced, only the last of a rapid sequence of
   *   submitted strings will actually be checked).
   */
  makeDebouncedHandleAvailabilityChecker() {
    return makeDebouncedAsyncValidator(async (handle: string) => {
      return await this.handleAvailabilityQuery.fetch(handle);
    }, 500);
  }

  /** @internal */
  idleLogoutMessage = defineMessage({
    defaultMessage: 'You have been inactive, and will be logged out',
    id: 'TokenizedSDK:idleLogoutMessage',
  });

  /*
  // Commented out - zxcvbn removed to reduce bundle size
  @internal
  zxcvbnFeedbackMessageMap: Record<string, MessageDescriptor> = defineMessages({
    'Use a few words, avoid common phrases': {
      defaultMessage: 'Use a few words, avoid common phrases',
      description: 'Strong password advice 1',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.1',
    },
    'No need for symbols, digits, or uppercase letters': {
      defaultMessage: 'No need for symbols, digits, or uppercase letters',
      description: 'Strong password advice 2',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.2',
    },
    'Add another word or two. Uncommon words are better.': {
      defaultMessage: 'Add another word or two. Uncommon words are better.',
      description: 'Strong password advice 3',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.3',
    },
    'Straight rows of keys are easy to guess': {
      defaultMessage: 'Straight rows of keys are easy to guess',
      description: 'Strong password advice 4',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.4',
    },
    'Short keyboard patterns are easy to guess': {
      defaultMessage: 'Short keyboard patterns are easy to guess',
      description: 'Strong password advice 5',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.5',
    },
    'Use a longer keyboard pattern with more turns': {
      defaultMessage: 'Use a longer keyboard pattern with more turns',
      description: 'Strong password advice 6',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.6',
    },
    'Repeats like "aaa" are easy to guess': {
      defaultMessage: 'Repeats like “aaa” are easy to guess',
      description: 'Strong password advice 7',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.7',
    },
    'Repeats like "abcabcabc" are only slightly harder to guess than "abc"': {
      defaultMessage:
        'Repeats like “abcabcabc” are only slightly harder to guess than “abc”',
      description: 'Strong password advice 8',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.8',
    },
    'Avoid repeated words and characters': {
      defaultMessage: 'Avoid repeated words and characters',
      description: 'Strong password advice 9',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.9',
    },
    'Sequences like abc or 6543 are easy to guess': {
      defaultMessage: 'Sequences like “abc” or “6543” are easy to guess',
      description: 'Strong password advice 10',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.10',
    },
    'Avoid sequences': {
      defaultMessage: 'Avoid sequences',
      description: 'Strong password advice 11',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.11',
    },
    'Recent years are easy to guess': {
      defaultMessage: 'Recent years are easy to guess',
      description: 'Strong password advice 12',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.12',
    },
    'Avoid recent years': {
      defaultMessage: 'Avoid recent years',
      description: 'Strong password advice 13',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.13',
    },
    'Avoid years that are associated with you': {
      defaultMessage: 'Avoid years that are associated with you',
      description: 'Strong password advice 14',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.14',
    },
    'Dates are often easy to guess': {
      defaultMessage: 'Dates are often easy to guess',
      description: 'Strong password advice 15',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.15',
    },
    'Avoid dates and years that are associated with you': {
      defaultMessage: 'Avoid dates and years that are associated with you',
      description: 'Strong password advice 16',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.16',
    },
    'This is a top-10 common password': {
      defaultMessage: 'This is a top-10 common password',
      description: 'Strong password advice 17',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.17',
    },
    'This is a top-100 common password': {
      defaultMessage: 'This is a top-100 common password',
      description: 'Strong password advice 18',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.18',
    },
    'This is a very common password': {
      defaultMessage: 'This is a very common password',
      description: 'Strong password advice 19',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.19',
    },
    'This is similar to a commonly used password': {
      defaultMessage: 'This is similar to a commonly used password',
      description: 'Strong password advice 20',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.20',
    },
    'A word by itself is easy to guess': {
      defaultMessage: 'A word by itself is easy to guess',
      description: 'Strong password advice 21',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.21',
    },
    'Names and surnames by themselves are easy to guess': {
      defaultMessage: 'Names and surnames by themselves are easy to guess',
      description: 'Strong password advice 22',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.22',
    },
    'Common names and surnames are easy to guess': {
      defaultMessage: 'Common names and surnames are easy to guess',
      description: 'Strong password advice 23',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.23',
    },
    "Capitalization doesn't help very much": {
      defaultMessage: 'Capitalization doesn’t help very much',
      description: 'Strong password advice 24',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.24',
    },
    'All-uppercase is almost as easy to guess as all-lowercase': {
      defaultMessage:
        'All-uppercase is almost as easy to guess as all-lowercase',
      description: 'Strong password advice 25',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.25',
    },
    "Reversed words aren't much harder to guess": {
      defaultMessage: 'Reversed words aren’t much harder to guess',
      description: 'Strong password advice 26',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.26',
    },
    "Predictable substitutions like '@' instead of 'a' don't help very much": {
      defaultMessage:
        'Predictable substitutions like “@” instead of “a” don’t help very much',
      description: 'Strong password advice 27',
      id: 'TokenizedSDK:analyzePasswordStrength.feedback.27',
    },
  });
  */

  /*
  // Commented out - zxcvbn removed to reduce bundle size
  @internal
  zxcvbnFeedbackMessagePositive: Record<string, boolean> = {
    'Use a few words, avoid common phrases': true,
    'No need for symbols, digits, or uppercase letters': false,
    'Add another word or two. Uncommon words are better.': true,
    'Straight rows of keys are easy to guess': false,
    'Short keyboard patterns are easy to guess': false,
    'Use a longer keyboard pattern with more turns': true,
    'Repeats like "aaa" are easy to guess': false,
    'Repeats like "abcabcabc" are only slightly harder to guess than "abc"':
      false,
    'Avoid repeated words and characters': false,
    'Sequences like abc or 6543 are easy to guess': false,
    'Avoid sequences': false,
    'Recent years are easy to guess': false,
    'Avoid recent years': false,
    'Avoid years that are associated with you': false,
    'Dates are often easy to guess': false,
    'Avoid dates and years that are associated with you': false,
    'This is a top-10 common password': false,
    'This is a top-100 common password': false,
    'This is a very common password': false,
    'This is similar to a commonly used password': false,
    'A word by itself is easy to guess': false,
    'Names and surnames by themselves are easy to guess': false,
    'Common names and surnames are easy to guess': false,
    "Capitalization doesn't help very much": false,
    'All-uppercase is almost as easy to guess as all-lowercase': false,
    "Reversed words aren't much harder to guess": false,
    "Predictable substitutions like '@' instead of 'a' don't help very much":
      false,
  };

  /*
  // Commented out - zxcvbn removed to reduce bundle size
  @internal
  mapZxcvbnMessage(message: string) {
    const mappedMessageFormat = this.zxcvbnFeedbackMessageMap[message];
    if (mappedMessageFormat) {
      return this.getIntl().formatMessage(mappedMessageFormat);
    }
    return message;
  }
  */

  /** The minimum length of password that will be accepted to secure an account */
  PASSWORD_MIN_LENGTH = 8;

  /**
   * Assess the strength of a password entered by the user, providing feedback
   * and suggestions for improvement. Use this in your “create account” and
   * “change password” dialogs. The `isAcceptable` property in the result
   * object specifies whether or not this password will be accepted by
   * [`createNewAccount`]{@link Account.createNewAccount}.
   *
   * The core analysis is performed by the [`zxcvbn`]{@link zxcvbn#}
   * package, with some additional formatting (including localization) and
   * feedback specific to the Tokenized service added to the result.
   *
   * @param password - The user-supplied password to check
   * @param userInputs - An optional list of strings that should be
   *   considered weak for the analysis. Use this to pass in the account
   *   identification (first and last names, email, handle), so that repeating
   *   those as the password is rejected.
   */
  analyzePasswordStrength = (
    password: string,
    userInputs?: string[],
  ): {
    isAcceptable: boolean;
    rejectionMessage: string | undefined;
    rejectionMessageIsNotSpecific: boolean;
    strengthScore: number;
    formattedStrength: string;
    suggestions: string[];
    suggestionsPositive: boolean[];
    zxcvbnAnalysis?: undefined;
  } => {
    const intl = this.getIntl();

    let formattedStrength = intl.formatMessage({
      defaultMessage: 'Extremely weak',
      description: 'Password strength: 0 out of 4',
      id: 'TokenizedSDK:analyzePasswordStrength.score.0',
    });
    let suggestions = [
      intl.formatMessage(
        {
          defaultMessage:
            'Use a password manager to generate {minLength, number}+ random characters',
          description: 'Strong password suggestion 1',
          id: 'TokenizedSDK:analyzePasswordStrength.fixedSuggestion.1',
        },
        { minLength: this.PASSWORD_MIN_LENGTH },
      ),
      intl.formatMessage({
        defaultMessage: 'Choose a random sequence of 4+ uncommon words',
        description: 'Strong password suggestion 2',
        id: 'TokenizedSDK:analyzePasswordStrength.fixedSuggestion.2',
      }),
      intl.formatMessage({
        defaultMessage: 'Avoid ordinary sentences, quotes, and lyrics',
        description: 'Strong password suggestion 3',
        id: 'TokenizedSDK:analyzePasswordStrength.fixedSuggestion.3',
      }),
      intl.formatMessage({
        defaultMessage: 'Don’t reuse a password you’ve used elsewhere',
        description: 'Strong password suggestion 4',
        id: 'TokenizedSDK:analyzePasswordStrength.fixedSuggestion.4',
      }),
    ];
    let suggestionsPositive = [true, true, false, false];

    if (
      !password ||
      typeof password !== 'string' ||
      password.length < this.PASSWORD_MIN_LENGTH
    ) {
      return {
        isAcceptable: false,
        rejectionMessage: intl.formatMessage(
          {
            defaultMessage: 'Must be {minChars, number} or more characters',
            description: 'Password rejection message when too short',
            id: 'TokenizedSDK:analyzePasswordStrength.tooShort',
          },
          { minChars: this.PASSWORD_MIN_LENGTH },
        ),
        rejectionMessageIsNotSpecific: false,
        strengthScore: 0,
        formattedStrength,
        suggestions,
        suggestionsPositive,
      };
    }

    let wordsToAvoid;
    if (Array.isArray(userInputs)) {
      wordsToAvoid = userInputs;
    }
    // const zxcvbnAnalysis = zxcvbn(password, wordsToAvoid); // Commented out - zxcvbn removed

    // Simplified password strength check without zxcvbn
    let strengthScore = 0;
    const hasLowerCase = /[a-z]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const length = password.length;

    // Calculate score based on complexity (0-4 scale)
    if (length >= 8) {
      strengthScore++;
    }
    if (length >= 12) {
      strengthScore++;
    }
    if (hasLowerCase && hasUpperCase) {
      strengthScore++;
    }
    if (hasNumbers && hasSpecial) {
      strengthScore++;
    }

    // Check against user inputs (simplified)
    let isAcceptable = strengthScore >= 2 && length >= this.PASSWORD_MIN_LENGTH;
    let rejectionMessage: string | undefined = undefined;
    let rejectionMessageIsNotSpecific = false;

    if (userInputs && userInputs.length > 0) {
      const lowerPassword = password.toLowerCase();
      for (const input of userInputs) {
        if (input && lowerPassword.includes(input.toLowerCase())) {
          isAcceptable = false;
          rejectionMessage = intl.formatMessage({
            defaultMessage: 'Password should not contain personal information',
            description: 'Password strength rejection: contains personal info',
            id: 'TokenizedSDK:analyzePasswordStrength.personalInfo',
          });
          break;
        }
      }
    }

    if (!isAcceptable && !rejectionMessage) {
      rejectionMessageIsNotSpecific = true;
      rejectionMessage = intl.formatMessage({
        defaultMessage: 'Too weak',
        description: 'Password strength rejection: no specific reason',
        id: 'TokenizedSDK:analyzePasswordStrength.rejected',
      });
    }

    // Map score to descriptions (0-4 scale)
    if (strengthScore >= 4) {
      formattedStrength = intl.formatMessage({
        defaultMessage: 'Excellent',
        description: 'Password strength: 4 out of 4',
        id: 'TokenizedSDK:analyzePasswordStrength.score.4',
      });
    } else if (strengthScore >= 3) {
      formattedStrength = intl.formatMessage({
        defaultMessage: 'Moderate',
        description: 'Password strength: 3 out of 4',
        id: 'TokenizedSDK:analyzePasswordStrength.score.3',
      });
    } else if (strengthScore >= 2) {
      formattedStrength = intl.formatMessage({
        defaultMessage: 'Weak',
        description: 'Password strength: 2 out of 4',
        id: 'TokenizedSDK:analyzePasswordStrength.score.2',
      });
    } else if (strengthScore >= 1) {
      formattedStrength = intl.formatMessage({
        defaultMessage: 'Very weak',
        description: 'Password strength: 1 out of 4',
        id: 'TokenizedSDK:analyzePasswordStrength.score.1',
      });
    }

    return {
      isAcceptable,
      rejectionMessage,
      rejectionMessageIsNotSpecific,
      strengthScore,
      formattedStrength,
      suggestions,
      suggestionsPositive,
    };
  };

  /** @internal */
  authenticationDevicesQuery = this.makeQuery(
    (query) => ({
      ...query,
      // Devices hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: this.getEnableQueriesValidDuringMfa(),
    }),
    async () => {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('users', '@me', 'devices'),
        undefined,
        selectPreAuthToken,
      );
      return schema.normalizeResponse(
        'Query authentication devices',
        response?.data?.data,
        schema.arrayOf(restApiDevice),
      ).normalizedValue;
    },
  );

  /** @internal */
  retrieveHandshakesAction(
    scope: TokenScopeType,
    clientId: string,
  ): TThunkAction<
    Promise<{
      data: THandshake;
      token?: TDecodedToken;
    }>
  > {
    return async (dispatch) => {
      if (!TOKEN_SCOPES.includes(scope)) {
        throw new TypeError(
          `Retrieve handshakes arguments: scope must be one of ${TOKEN_SCOPES.map(
            (scope) => `'${scope}'`,
          ).join()}`,
        );
      }
      if (!clientId || typeof clientId !== 'string') {
        throw new TypeError(
          'Retrieve handshakes arguments: clientId must be a string',
        );
      }

      // Note: return value looks like:
      // {
      //   data: {
      //     status: 'approved',
      //     ...,
      //   },
      //   token: {
      //     token: 'TOKEN',
      //   }
      // }
      const response = await this.tokenizedApi.endpoints.get<{
        data: Record<string, unknown>;
        token?: {
          token?: string;
        };
      }>(
        urlPath('auth', 'handshakes', 'poll'),
        {
          params: {
            filter: {
              scope,
              client_id: clientId,
            },
          },
        },
        selectPreAuthToken,
      );
      const handshake = schema.normalizeResponse(
        'Retrieve handshakes',
        response?.data?.data,
        restApiHandshake,
      ).normalizedValue;

      if (!handshake?.status) {
        throw new Error('Please log in again');
      }
      switch (handshake?.status) {
        case 'pending':
          break;
        case 'approved':
          if (scope === 'login') {
            this.checkClockSkew(response);
            const checkedToken = decodeAndCheckToken(
              response?.data?.token?.token,
            );
            if (checkedToken.tokenIsFullyAuthorized) {
              dispatch(
                accountSlice.actions.retrieveLogInHandshakeTokenResponse({
                  ...checkedToken,
                  fromDeviceId: handshake.deviceId,
                }),
              );
              return {
                data: handshake,
                token: checkedToken,
              };
            }
            throw new TokenizedResponseInvalidError(
              'Retrieve log in handshake: response invalid',
            ).withFormatting(this);
          }
          break;
        case 'rejected':
          throw new TokenizedLogInDeniedError().withFormatting(this);
        case 'cancelled':
          throw new TokenizedLogInCancelledError().withFormatting(this);
        default:
          throw new TokenizedResponseInvalidError(
            'Retrieve handshakes: response invalid',
          ).withFormatting(this);
      }
      return {
        data: handshake,
      };
    };
  }

  /** @internal */
  initiateDevicePairingAction(currentDeviceCount: number): TThunkAction {
    return async (dispatch, getState) => {
      if (!selectPreAuthToken(getState())) {
        throw new Error(
          'Sign in to an account before attempting device pairing',
        );
      }
      dispatch(
        accountSlice.actions.startDevicePairing({
          currentDeviceCount,
        }),
      );
    };
  }

  /** @internal */
  ensureDevicePairingAction(): TThunkAction<Promise<boolean>> {
    return async (dispatch, getState) => {
      const devices = await this.authenticationDevicesQuery.fetchWithOptions({
        staleTime: 0,
      });
      if (selectIsWaitingForDevicePairing(getState())) {
        if (
          (devices?.length || 0) >
          (selectPairingPreviousDeviceCount(getState()) || 0)
        ) {
          dispatch(accountSlice.actions.stopDevicePairing());
        }
      } else {
        if (!devices?.length) {
          await dispatch(
            this.initiateDevicePairingAction(devices?.length || 0),
          );
        }
      }
      return !!devices?.length;
    };
  }

  /** @internal */
  checkAuthenticatorVersion(): TThunkAction<Promise<boolean>> {
    return async (dispatch, getState) => {
      let deviceVersionString;
      const deviceId = selectDeviceIdThatApprovedLogin(getState());
      if (deviceId) {
        const devices = await this.authenticationDevicesQuery.fetchWithOptions({
          staleTime: 0,
        });
        deviceVersionString = devices?.find(
          (device) => device.id === deviceId,
        )?.client;
      }

      let authenticatorVersionSupported = isAuthenticatorVersionSupported(
        deviceVersionString,
        [
          Account.MIN_SUPPORTED_AUTHENTICATOR_VERSION,
          selectPermissionRestrictionsAtLogIn(getState())?.find(
            (restriction) =>
              restriction.topic === 'ensureMinAuthenticatorVersion',
          )?.code,
        ],
      );

      if (FORCE_AUTHENTICATOR_UNSUPPORTED) {
        authenticatorVersionSupported = false;
      }

      dispatch(
        accountSlice.actions.logInProcessUpdateChecklist({
          authenticatorVersionSupported,
        }),
      );
      return authenticatorVersionSupported;
    };
  }

  /** @internal */
  checkRootKeyCreatedAction(): TThunkAction<Promise<boolean>> {
    return async (dispatch) => {
      let rootKeyCreated;
      try {
        const response = await this.tokenizedApi.endpoints.get(
          urlPath('users', '@me', 'rootkeys'),
        );
        rootKeyCreated = !!response?.data?.data?.length;
      } catch (error) {
        // console.error('Error checking root keys', error);
        rootKeyCreated = false;
      }
      if (rootKeyCreated !== undefined) {
        dispatch(
          accountSlice.actions.logInProcessUpdateChecklist({
            rootKeyCreated,
          }),
        );
      }
      return rootKeyCreated;
    };
  }

  topicPermissionQuery = this.makeQuery(
    (query, topic?: string) => ({
      ...query,
      staleTime: 0,
      enabled: query.enabled && !!topic,
    }),
    async (context, topic?: string) => {
      if (!topic) {
        return null;
      }
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('users', '@me', 'permission', topic),
      );
      if (TokenizedApi.TEST_HEALTH_CHECK && topic === 'ensureBackupsHealthy') {
        if (!(await this.userDetailsQuery.fetch())?.isBackedUp) {
          return { topic: 'ensureBackupsHealthy' };
        } else {
          return null;
        }
      }
      return schema.normalizeResponse(
        'Permission',
        response?.data?.data,
        restApiPermissionRestrictionMessage,
      ).normalizedValue;
    },
  );

  /** @internal */
  ensureBackupsHealthy(): TThunkAction<Promise<boolean>> {
    return async (dispatch) => {
      let backupsHealthy = true;
      try {
        const message = await this.topicPermissionQuery.fetch(
          'ensureBackupsHealthy',
        );
        backupsHealthy = message?.topic !== 'ensureBackupsHealthy';
        if (TokenizedApi.TEST_HEALTH_CHECK) {
          backupsHealthy = !!(
            await this.userDetailsQuery.fetchWithOptions({
              staleTime: 0,
              retry: false,
            })
          )?.isBackedUp;
        }
      } catch (error) {
        // console.error('Error checking backups healthy', error);
      }
      dispatch(
        accountSlice.actions.logInProcessUpdateChecklist({
          backupsHealthy,
        }),
      );
      return backupsHealthy;
    };
  }

  stopDevicePairing() {
    this.tokenizedApi.state.dispatch(accountSlice.actions.stopDevicePairing());
  }

  userDetailsQuery = this.makeQuery(
    (query) => ({
      ...query,
      // User details hardly ever change: Wait 30 minutes before re-query
      staleTime: minutes(30),
      enabled: this.getEnableQueriesValidDuringMfa(),
    }),
    async () => {
      const response = await this.tokenizedApi.endpoints.get(
        urlPath('users', '@me'),
        undefined,
        selectPreAuthToken,
      );
      const user = schema.normalizeResponse(
        'Query user details',
        response?.data?.data,
        restApiUser,
      ).normalizedValue;

      if (!user) {
        return user;
      }

      // If phone verification is disabled, phone numbers collected
      // during sign-up are prepended with '!'
      let phoneIsVerified = undefined;
      if (user.phone) {
        phoneIsVerified = !user.phone.startsWith('!');
      }
      return {
        ...user,
        phone: phoneIsVerified === false ? user.phone.slice(1) : user.phone,
        phoneIsVerified,
      };
    },
  );

  async updateUserEmail({
    email,
    setMfaRequest,
  }: {
    email: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!email || typeof email !== 'string') {
      throw new TypeError(
        'Update user email: email must be a non-empty string',
      );
    }

    const { clientId, sourceName } =
      await this.tokenizedApi.getApplicationIdentifiers();

    const response = await this.tokenizedApi.endpoints.put(
      urlPath('users', '@me', 'email'),
      { email, client_id: clientId, source_name: sourceName },
    );
    const handshake = schema.normalizeResponse(
      'Update user email',
      response?.data?.data,
      restApiHandshakeRequiredNoActivity,
    ).normalizedValue;
    if (!handshake?.handshakeId) {
      throw new Error('Mobile authenticator confirmation request failed');
    }

    await this.tokenizedApi.mfa.waitForMfa(
      handshake.handshakeId,
      setMfaRequest,
    );

    return handshake;
  }

  async updateUserDevice({
    phoneNumber,
    setMfaRequest,
  }: {
    phoneNumber: string;
    setMfaRequest: (mfaRequest: MFARequest | null) => void;
  }) {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
      throw new TypeError(
        'Update user device: phoneNumber must be a non-empty string',
      );
    }

    const { clientId, sourceName } =
      await this.tokenizedApi.getApplicationIdentifiers();

    const response = await this.tokenizedApi.endpoints.put(
      urlPath('users', '@me', 'devices'),
      {
        phone_number: phoneNumber,
        client_id: clientId,
        source_name: sourceName,
      },
    );
    const handshake = schema.normalizeResponse(
      'Update user device',
      response?.data?.data,
      restApiHandshakeRequiredNoActivity,
    ).normalizedValue;
    if (!handshake?.handshakeId) {
      throw new Error('Mobile authenticator confirmation request failed');
    }

    await this.tokenizedApi.mfa.waitForMfa(
      handshake.handshakeId,
      setMfaRequest,
    );

    return handshake;
  }

  async requestDeviceVerification({
    countryCode,
    nationalPhoneNumber,
    deviceId,
  }: {
    countryCode: string;
    nationalPhoneNumber: string;
    deviceId: string;
  }) {
    if (!countryCode || typeof countryCode !== 'string') {
      throw new TypeError(
        'Device verification: countryCode must be a non-empty string',
      );
    }
    if (!nationalPhoneNumber || typeof nationalPhoneNumber !== 'string') {
      throw new TypeError(
        'Device verification: nationalPhoneNumber must be a non-empty string',
      );
    }

    let verificationDeviceId = deviceId;

    if (!deviceId) {
      const activeDevice = (
        await this.authenticationDevicesQuery.fetch()
      )?.find((device) => device.isActive);
      if (!activeDevice) {
        throw new Error('No active device for verification');
      }
      verificationDeviceId = activeDevice.id;
    }

    await this.tokenizedApi.endpoints.put(
      urlPath('users', '@me', 'devices', 'verify'),
      {
        country_code: countryCode,
        phone_number: nationalPhoneNumber,
        id: verificationDeviceId,
      },
    );
  }

  async completeDeviceVerification({
    countryCode,
    nationalPhoneNumber,
    code,
  }: {
    countryCode: string;
    nationalPhoneNumber: string;
    code: string;
  }) {
    if (!countryCode || typeof countryCode !== 'string') {
      throw new TypeError(
        'Device verification: countryCode must be a non-empty string',
      );
    }
    if (!nationalPhoneNumber || typeof nationalPhoneNumber !== 'string') {
      throw new TypeError(
        'Device verification: nationalPhoneNumber must be a non-empty string',
      );
    }
    if (!code || typeof code !== 'string') {
      throw new TypeError(
        'Device verification: code must be a non-empty string',
      );
    }

    await this.tokenizedApi.endpoints.post(
      urlPath('users', '@me', 'devices', 'verify'),
      {
        country_code: countryCode,
        phone_number: nationalPhoneNumber,
        otp_secret: code,
      },
    );

    this.userDetailsQuery.invalidate();
  }

  async verify(scope: TVerificationCodeScope, code: string) {
    if (!scope || typeof scope !== 'string' || !VERIFY_SCOPES.includes(scope)) {
      throw new TypeError(
        `Verification: scope must be one of ${VERIFY_SCOPES.join(', ')}`,
      );
    }
    if (!code || typeof code !== 'string') {
      throw new TypeError('Verification: code must be a non-empty string');
    }

    await this.tokenizedApi.endpoints.post(urlPath('auth', 'verify', scope), {
      code,
    });
  }

  // This is called automatically after construction
  // to complete the loading process
  /** @internal */
  restoreSessionAction(): TThunkAction<Promise<boolean>> {
    return async (dispatch, getState) => {
      if (!selectIsLoading(getState())) {
        throw new Error('Can’t restore session after startup');
      }

      try {
        if (this.tokenizedApi.config.mobileAuthToken) {
          if (typeof this.tokenizedApi.config.mobileAuthToken === 'function') {
            dispatch(
              accountSlice.actions.requestAuthTokenResponse({
                token: null,
                tokenIsFullyAuthorized: true,
                tokenExpiryTimestamp: undefined,
                allowAllQueries: true,
              }),
            );
          } else {
            const checkedToken = decodeAndCheckToken(
              this.tokenizedApi.config.mobileAuthToken,
            );
            dispatch(
              accountSlice.actions.requestAuthTokenResponse({
                ...checkedToken,
                allowAllQueries: true,
              }),
            );
          }
          return true;
        }

        let token: string | null = null;
        if (
          typeof this.tokenizedApi.config.restoreAuthTokenCallback ===
          'function'
        ) {
          token = await this.tokenizedApi.config.restoreAuthTokenCallback();
        }

        if (this.tokenizedApi.config.enableSessionPersistence) {
          token = this.tokenizedApi.persistentState.getItem(
            AUTH_TOKEN_PERSIST_KEY,
          );
        }
        const checkedToken = decodeAndCheckToken(token);
        if (!checkedToken.tokenIsFullyAuthorized) {
          throw new TokenizedAuthError().withFormatting(this);
        }
        dispatch(accountSlice.actions.restoreSession(checkedToken));

        // Get user details as soon as we can
        await this.userDetailsQuery.fetchWithOptions({ staleTime: 0 });

        await this.tokenizedApi.mfa.clearupExpiredHandshakes();

        // All good, so start receiving activity events
        this.tokenizedApi.activity.startListening();
      } catch (error) {
        // console.error(error);
        // If a previous session can’t be restored we don’t want
        // to report an error, just quietly enter the logged-out state
        dispatch(accountSlice.actions.logOut());
        return false;
      } finally {
        dispatch(accountSlice.actions.loadingEnd());
      }
      return true;
    };
  }

  /**
   * Register an email address.
   * This will trigger a verification code to be sent to this email address
   */
  async registerEmail({
    email,
  }: {
    /** Each account is identified by a unique verified email */
    email: string;
  }) {
    if (!email || typeof email !== 'string') {
      throw new TypeError(
        'Create new account arguments: email must be a non-empty string',
      );
    }

    try {
      await this.tokenizedApi.endpoints.post(
        urlPath('auth', 'register', 'email'),
        {
          email,
        },
        undefined,
        null, // no authentication required for this endpoint
      );
    } catch (error) {
      if (
        // A field validation error indicates the
        // email address in blocked by the allow list
        error instanceof TokenizedValidationError
      ) {
        throw new TokenizedSignUpEmailRestrictedError().withFormatting(this);
      } else {
        throw error;
      }
    }

    return true;
  }

  /**
   * Register a phone number
   * This will trigger a verification code to be sent to this phone number.
   */
  async registerPhone({ phone }: { phone: string }) {
    if (!phone || typeof phone !== 'string') {
      throw new TypeError(
        'Create new account arguments: phone must be a non-empty string',
      );
    }

    const response = await this.tokenizedApi.endpoints.post(
      urlPath('auth', 'register', 'phone'),
      {
        phone,
      },
      undefined,
      selectPreAuthToken,
    );

    if (response?.status === 201) {
      // Indicates phone number verification is not required, and
      // the account is ready to log in
      return { isVerified: true };
    }

    return { isVerified: false };
  }

  /**
   * Create an account with a verified email address and password
   */
  async createNewAccount({
    password,
    emailVerificationCode,
  }: {
    password: string;
    emailVerificationCode: string;
  }) {
    if (!password || typeof password !== 'string') {
      throw new TypeError(
        'Create new account arguments: password must be a non-empty string',
      );
    }
    const passwordAnalysis = this.analyzePasswordStrength(password);
    if (!passwordAnalysis.isAcceptable) {
      throw new Error(
        `Create new account password: ${passwordAnalysis.rejectionMessage}`,
      );
    }

    if (typeof emailVerificationCode !== 'string') {
      throw new TypeError(
        'Create new account arguments: email code must be a non-empty string',
      );
    }

    const hashedPassword = await hmac(password, LOGIN_PASSWORD_HASH);

    const response = await this.tokenizedApi.endpoints.post(
      urlPath('auth', 'register', 'user'),
      {
        password: hashedPassword,
        email_verification_code: emailVerificationCode,
      },
      undefined,
      null, // no authentication required for this endpoint
    );

    this.checkClockSkew(response);

    // This token is used to handle email and SMS code entry
    // (before the main log-in process begins)
    this.supplyToken(response.data.data.token as string);

    return true;
  }

  /**
   * Create the user's personal profile
   */
  async createProfile({ name, handle }: { name: string; handle: string }) {
    const postfix = this.tokenizedApi.config.userHandleDomain
      ? this.tokenizedApi.account.getUserHandlePostfix()
      : '';
    //@TODO error check

    await this.tokenizedApi.endpoints.post(
      urlPath('auth', 'register', 'profile'),
      {
        name,
        handle: handle + postfix,
      },
      undefined,
      selectPreAuthToken,
    );

    this.userDetailsQuery.invalidate();

    this.tokenizedApi.state.dispatch(
      accountSlice.actions.logInProcessUpdateChecklist({
        handleCreated: true,
      }),
    );

    // Note: personal profile creation is done during the polling phase of
    // the log-in process, which will continue to the next step once it
    // notices the handleCreated checklist item is true
  }

  /**
   *
   */
  async verifyPhone({
    phoneVerificationCode,
  }: {
    phoneVerificationCode: string;
  }) {
    if (typeof phoneVerificationCode !== 'string') {
      throw new TypeError(
        'Create new account arguments: phone code must be a non-empty string',
      );
    }

    await this.tokenizedApi.endpoints.post(
      urlPath('auth', 'verify', 'userPhone'),
      {
        code: phoneVerificationCode,
      },
      undefined,
      selectPreAuthToken,
    );

    return true;
  }

  parseIdentifierString(identifier?: string): TParsedAccountIdentifier {
    if (typeof identifier !== 'string') {
      return {
        errorType: 'invalid',
        error: new TypeError('Account identifier must be a string'),
      };
    }

    const parts = identifier.trim().split('@');

    if (parts.length > 2) {
      return {
        errorType: 'multipleAts',
        error: new Error('Account identifier has multiple @ characters'),
      };
    }
    if (!parts[0]) {
      return {
        errorType: 'incomplete',
        error: new Error('Incomplete account identifier'),
      };
    }

    if (parts.length > 1) {
      if (!parts[1]) {
        return {
          errorType: 'incomplete',
          error: new Error('Incomplete account identifier'),
        };
      }
      if (parts[1].endsWith('tkz.id')) {
        for (const [handleBackend, handleDomain] of Object.entries(
          handleDomains,
        )) {
          if (parts[1] === handleDomain) {
            if (handleBackend !== this.tokenizedApi.config.tokenizedBackend) {
              return {
                errorType: 'handleDomainNotConfigured',
                error: new Error('Not configured to access this account'),
                identifierType: 'handle',
                identifier: `${parts[0]}@${handleDomain}`,
                handle: parts[0],
                handleBackend: handleBackend as TBackend,
                handleDomain,
              };
            }
            return {
              identifierType: 'handle',
              identifier: `${parts[0]}@${handleDomain}`,
              handle: parts[0],
              handleBackend: handleBackend as TBackend,
              handleDomain,
            };
          }
        }
        return {
          errorType: 'handleDomainUnsupported',
          error: new Error(`Unsupported handle domain ${parts[1]}`),
          identifierType: 'handle',
          identifier: `${parts[0]}@${parts[1]}`,
          handle: parts[0],
          handleDomain: parts[1],
        };
      }

      return {
        identifierType: 'email',
        identifier: `${parts[0]}@${parts[1]}`,
      };
    }

    return {
      identifierType: 'handle',
      identifier: `${parts[0]}@${
        handleDomains[this.tokenizedApi.config.tokenizedBackend]
      }`,
      handle: parts[0],
      handleBackend: this.tokenizedApi.config.tokenizedBackend,
      handleDomain: handleDomains[this.tokenizedApi.config.tokenizedBackend],
    };
  }

  /**
   * **`Redux action creator`** Begin a new asynchronous log in process with the
   * specified account credentials. If a log in attempt is currently in
   * progress, it will be cancelled and replaced with the new one. Fails if
   * there’s already a valid authenticated session.
   *
   * @returns This is an asynchronous action creator (a thunk).
   *   Dispatch the function to start the log-in process. A `Promise` will be
   *   returned from `dispatch` that resolves to one of three possible values:
   *
   *   - `undefined` – indicates the log-in process was cancelled by a later log-in request.
   *   - `false` – indicates the log-in process is paused waiting for a user interaction.
   *   - `true` – indicates log in completed successfully.
   *
   * @internal
   */
  logInAction(args?: {
    /**
     * Specify a handle (e.g. `hankrearden`), paymail (e.g.
     * `hankrearden@tkz.id`), or the account email address.
     */
    identifier: string;
    password: string;
  }): TThunkAction<Promise<undefined | false | true>> {
    return async (dispatch, getState) => {
      if (selectIsLoggedIn(getState())) {
        throw new Error('Can’t log in: A session is already active');
      }

      // STEP 1: Start log-in process
      let logInProcessId = selectLogInProcessId(getState());
      let isNewLogInProcess = false;
      if (!logInProcessId) {
        const password = args?.password;
        const { error, identifierType, identifier } =
          this.parseIdentifierString(args?.identifier);
        if (error || !identifierType || !identifier) {
          throw error || new Error('Invalid account identifier');
        }
        if (!password || typeof password !== 'string') {
          throw new TypeError(
            'Log in arguments: password must be a non-empty string',
          );
        }

        const logInPassword = await hmac(password, LOGIN_PASSWORD_HASH);

        logInProcessId = uuidv4();
        dispatch(
          accountSlice.actions.logInProcessStart({
            logInProcessId,
            identifier,
            identifierType,
            logInPassword,
          }),
        );
        isNewLogInProcess = true;
      }

      try {
        const { clientId, sourceName } =
          await this.tokenizedApi.getApplicationIdentifiers();

        // STEP 2: Obtain pre-auth token and start log-in MFA
        // Note that specifically in the case where this log-in process follows
        // on from a new account creation, we already have an auth token (so
        // the next step will not be requestToken), but there is no log-in
        // handshake active, so we choose to always request a new token if
        // this is a new log-in process
        if (
          selectLogInProcessNextStep(getState()) === 'requestToken' ||
          isNewLogInProcess
        ) {
          const identifier = selectLogInIdentifier(getState());
          if (!identifier || typeof identifier !== 'string') {
            throw new Error('Continue log in: unknown identifier');
          }
          const loginPassword = selectLogInPassword(getState());
          if (!loginPassword || typeof loginPassword !== 'string') {
            throw new Error('Continue log in: unknown password');
          }
          try {
            await dispatch(
              this.requestAuthTokenAction(
                clientId,
                sourceName,
                identifier,
                loginPassword,
              ),
            );
          } catch (error) {
            if (
              // An account with an unverified email causes
              // a very specific field validation error
              error instanceof TokenizedValidationError &&
              error.fieldErrors &&
              Object.keys(error.fieldErrors).length === 1 &&
              error.fieldErrors.email === 'unconfirmed'
            ) {
              throw new TokenizedLogInInvalidAccount().withFormatting(this);
            } else {
              throw error;
            }
          }

          // For security reasons, forget the log-in password once we have a token
          dispatch(accountSlice.actions.clearLogInPassword());

          // Check for cancellation of the process before next step
          if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
            return undefined;
          }
        }

        // User details are available as soon as we have an auth token
        // (doesn’t require MFA) – can be used to show name on welcome
        // screen during MFA
        const userDetails = await this.userDetailsQuery.fetchWithOptions({
          staleTime: 0,
        });

        // Check whether this is a new account that still
        // needs a handle (and username) to be set up
        if (selectLogInProcessNextStep(getState()) === 'handleCreation') {
          // Check for cancellation of the process before next step
          if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
            return undefined;
          }
          const handleCreated = !!userDetails?.displayHandle;
          dispatch(
            accountSlice.actions.logInProcessUpdateChecklist({
              handleCreated,
            }),
          );
        }

        // Steps 3—6 (handle creation, device pairing, MFA, root key creation),
        // are polled through to completion within this function
        while (selectLogInNeedsPolling(getState())) {
          // STEP 3: If the next step is 'handleCreation', we continue looping
          // until either the user creates the profile, or the token expires

          // STEP 4: Start device pairing if there are no paired devices
          if (selectLogInProcessNextStep(getState()) === 'devicePairing') {
            const devicePaired = await dispatch(
              this.ensureDevicePairingAction(),
            );
            // Check for cancellation of the process
            if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
              return undefined;
            }
            dispatch(
              accountSlice.actions.logInProcessUpdateChecklist({
                devicePaired,
              }),
            );
          }

          // STEP 5: Multi-factor auth – get a full auth token
          // by confirming with the paired authenticator
          if (selectLogInProcessNextStep(getState()) === 'mfa') {
            await dispatch(this.retrieveHandshakesAction('login', clientId));
            // Check for cancellation of the process
            if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
              return undefined;
            }
          }

          if (
            selectLogInProcessNextStep(getState()) === 'authenticatorUpdate'
          ) {
            // Step 6: Inspect the version of the mobile app that confirmed the
            // login handshake, and prompt the user if an update is needed.
            await dispatch(this.checkAuthenticatorVersion());
            // Check for cancellation of the process
            if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
              return undefined;
            }
            if (!selectLogInNeedsPolling(getState())) {
              break;
            }
          }

          if (selectLogInProcessNextStep(getState()) === 'rootKeyCreation') {
            // Step 7: Once we have a fully-authed token, we can check whether
            // the account has a root key. If not then we prevent log-in
            // until the key has been created (in the mobile app).
            await dispatch(this.checkRootKeyCreatedAction());
            // Check for cancellation of the process
            if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
              return undefined;
            }
            if (!selectLogInNeedsPolling(getState())) {
              break;
            }
          }

          if (selectLogInProcessNextStep(getState()) === 'backupHealthCheck') {
            // Step 8: Test whether a manual backup health check is required.
            await dispatch(this.ensureBackupsHealthy());
            // Check for cancellation of the process
            if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
              return undefined;
            }
            if (!selectLogInNeedsPolling(getState())) {
              break;
            }
          }

          await delayForSeconds(HANDSHAKE_POLL_INTERVAL_SECS);
          // Check for cancellation of the process
          if (!selectIsLogInProcessActive(getState(), logInProcessId)) {
            return undefined;
          }
        }

        // Check for success
        if (!selectLogInIsComplete(getState())) {
          // If we weren’t successful, then stop and log out
          throw new TokenizedAuthError().withFormatting(this);
        }

        // Force a reload of our own contact details, since they might
        // have been cached before they were set during profile creation
        this.tokenizedApi.relationships.handleQuery.invalidate();

        // All done, so end the log-in process and start the session, and
        // persist the auth token and wallet password to local storage so
        // the session can be restored
        this.persistAuthTokenForSessionRestore();
        dispatch(accountSlice.actions.logInProcessEnd(logInProcessId));
        this.tokenizedApi.activity.startListening();

        await this.tokenizedApi.mfa.clearupExpiredHandshakes();
      } catch (error) {
        // Errors that aren’t directly handled above cause a failure
        // and a return to the logged-out state
        dispatch(accountSlice.actions.logInProcessError(error));
        await dispatch(this.logOutAction());
        dispatch(accountSlice.actions.logOut());
        return false;
      }

      return true;
    };
  }

  /**
   * Begin a new asynchronous log in process with the specified account
   * credentials. If a log in attempt is currently in progress, it will be
   * cancelled and replaced with the new one. Fails if there’s already a valid
   * authenticated session.
   *
   * @returns A `Promise` that resolves to one of three
   *   possible values:
   *
   *   - `undefined` – indicates the log-in process was cancelled by a later log-in request.
   *   - `false` – indicates the log-in process is paused waiting for a user
   *       interaction, for example email verification.
   *   - `true` – indicates log in completed successfully.
   */
  logIn(options: {
    /**
     * Specify a handle (e.g. `hankrearden`), paymail (e.g.
     * `hankrearden@tkz.id`), or the account email address.
     */
    identifier: string;
    password: string;
  }) {
    return this.tokenizedApi.state.dispatch(this.logInAction(options));
  }

  clearLogInError() {
    this.tokenizedApi.state.dispatch(accountSlice.actions.clearLogInError());
  }

  /**
   * **`Redux action creator`** Use this to implement a “forgot password” flow
   * in your sign in dialog. Call this action, identifying the account using
   * its email address. If the account is recognized, the back end will send
   * a confirmation code to the email address. Call
   * [`resetPasswordAction`]{@link Account.resetPasswordAction}
   * with the entered confirmation code and new password to complete the process.
   *
   * @param email - Specify the account’s email address.
   *
   * @internal
   */
  requestPasswordResetAction(email: string): TThunkAction<Promise<void>> {
    return async (dispatch, getState) => {
      if (selectIsLoggingIn(getState())) {
        throw new Error('Password reset is not allowed while signing in');
      }
      const parsedIdentifier = this.parseIdentifierString(email);
      if (
        parsedIdentifier.error ||
        parsedIdentifier.identifierType !== 'email' ||
        !parsedIdentifier.identifier
      ) {
        throw parsedIdentifier.error || new Error('Invalid account email');
      }
      const identifier = parsedIdentifier.identifier;

      let response;
      try {
        response = await this.tokenizedApi.endpoints.post(
          urlPath('auth', 'recover', 'password'),
          {
            email: identifier,
          },
          undefined,
          null, // no auth token required
        );
      } catch (error) {
        if (
          error instanceof TokenizedApiError &&
          error?.fromEndpointError?.status === 404
        ) {
          throw new TokenizedLogInInvalidAccount().withFormatting(this);
        }
        throw error;
      }
      schema.normalizeResponse(
        'Request password reset',
        response?.data?.data,
        restApiAccountRecoveryDetails,
      );

      dispatch(
        accountSlice.actions.startResetPassword({
          identifier,
        }),
      );
    };
  }

  /**
   * Use this to implement a “forgot password” flow in your sign in dialog.
   * Call this method, identifying the account using its email address.
   * If the account is recognized, the back end will send a confirmation code to
   * the email address. Call
   * [`resetPassword`]{@link Account.resetPassword}
   * with the entered confirmation code and new password to complete the process.
   *
   * @param email - Specify the account’s email address.
   */
  requestPasswordReset(email: string) {
    return this.tokenizedApi.state.dispatch(
      this.requestPasswordResetAction(email),
    );
  }

  /**
   * **`Redux action creator`** Use this to set a new password for an account
   * when the user has forgotten their password. After first calling
   * [`requestPasswordResetAction`]{@link Account.requestPasswordResetAction},
   * prompt the user to enter the confirmation code, and create a new
   * password. On success, the action modifies the password, and
   * automatically starts a log-in process to the account.
   *
   * @returns This is an asynchronous action creator (a thunk).
   *   Dispatch the function to do the reset.
   *
   * @internal
   */
  resetPasswordAction({
    emailVerificationCode,
    password,
  }: {
    /** The confirmation code from the received email. */
    emailVerificationCode: string;
    /** The new password for the account. */
    password: string;
  }): TThunkAction<Promise<true>> {
    return async (dispatch, getState) => {
      const identifier = selectResetPasswordIdentifier(getState());
      if (!identifier) {
        throw new Error('Send a password reset request first');
      }

      if (!emailVerificationCode || typeof emailVerificationCode !== 'string') {
        throw new TypeError(
          'Reset password arguments: code must be a non-empty string',
        );
      }
      if (!password || typeof password !== 'string') {
        throw new TypeError(
          'Reset password arguments: password must be a non-empty string',
        );
      }
      const passwordAnalysis = this.analyzePasswordStrength(password);
      if (!passwordAnalysis.isAcceptable) {
        throw new Error(`Reset password: ${passwordAnalysis.rejectionMessage}`);
      }

      const hashedPassword = await hmac(password, LOGIN_PASSWORD_HASH);

      try {
        await this.tokenizedApi.endpoints.post(
          urlPath('auth', 'reset', emailVerificationCode),
          {
            new_password: hashedPassword,
            new_password_confirm: hashedPassword,
          },
          undefined,
          null, // no auth token required
        );
      } catch (error) {
        if (
          error instanceof TokenizedApiError &&
          error?.fromEndpointError?.status === 404
        ) {
          throw new TokenizedAccountVerificationInvalidError({
            matchesWithFieldName: 'emailVerificationCode',
            fromError: error,
          });
        }
        throw error;
      }

      if (selectIsLoggedIn(getState())) {
        dispatch(accountSlice.actions.cancelResetPassword());
      } else {
        // In the case where the user couldn’t get into their account:
        // If the reset was successful, start a log-in to the account using
        // the identifier we recorded earlier. We don’t wait for the login
        // process here – return success so that the caller can switch to their log-in UI.
        dispatch(this.logInAction({ identifier, password }));
      }

      return true;
    };
  }

  /**
   * Use this to set a new password for an account when the user has forgotten
   * their password. After first calling
   * [`requestPasswordReset`]{@link Account.requestPasswordReset},
   * prompt the user to enter the confirmation code, and create a new
   * password. On success, the method modifies the password, and
   * automatically starts a log-in process to the account.
   */
  resetPassword(options: {
    /** The confirmation code from the received email. */
    emailVerificationCode: string;
    /** The new password for the account. */
    password: string;
  }) {
    return this.tokenizedApi.state.dispatch(this.resetPasswordAction(options));
  }

  /**
   * Generate a new one-time password to start the device pairing process.
   * During sign in, this will be done automatically if the user has no active
   * authenticator devices. Use this function when the user explicitly chooses
   * to re-pair, or if you need to regenerate an expired code.
   */
  async initiateDevicePairing() {
    const devices = await this.authenticationDevicesQuery.fetchWithOptions({
      staleTime: 0,
    });
    return await this.tokenizedApi.state.dispatch(
      this.initiateDevicePairingAction(devices?.length || 0),
    );
  }

  /** @internal */
  ensureTokenRefreshAction(): TThunkAction<Promise<TDecodedToken | undefined>> {
    return async (dispatch, getState) => {
      // Only refresh fully-authorized tokens
      if (
        !!selectAuthToken(getState()) &&
        selectTokenNeedsRefresh(getState())
      ) {
        const response = await this.tokenizedApi.endpoints.get(
          urlPath('auth', 'token', 'renew'),
        );
        const normalized = schema.normalizeResponse(
          'Renew token',
          response?.data?.data,
          restApiRenewedToken,
        );
        this.checkClockSkew(response);
        const checkedToken = decodeAndCheckToken(
          normalized.normalizedValue?.token,
        );
        if (!checkedToken.tokenIsFullyAuthorized) {
          throw new TokenizedResponseInvalidError(
            'Renew token: response invalid',
          ).withFormatting(this);
        }
        dispatch(accountSlice.actions.renewTokenResponse(checkedToken));
        if (selectIsLoggedIn(getState())) {
          this.persistAuthTokenForSessionRestore();
        }
        return checkedToken;
      }
    };
  }

  ensureTokenRefresh() {
    return this.tokenizedApi.state.dispatch(this.ensureTokenRefreshAction());
  }

  /**
   * **`Redux action creator`** End the current authenticated session and clear
   * all internal state associated with it. If a log in attempt is currently in
   * progress then cancel it.
   *
   * @returns {function} This is an asynchronous action creator (a thunk).
   *   Dispatch the function to log out.
   *
   * @internal
   */
  logOutAction(): TThunkAction<Promise<void>> {
    return async (dispatch, getState) => {
      const wasLoggedIn = selectIsLoggedIn(getState());
      this.tokenizedApi.activity.stopListening();
      this.tokenizedApi.persistentState.removeItem(AUTH_TOKEN_PERSIST_KEY);
      dispatch(accountSlice.actions.logOut());
      // It's not safe to clear the react-query client inside a query
      //this.tokenizedApi.getQueryClient().clear();
      if (wasLoggedIn) {
        this.onLogOut?.();
      }
    };
  }

  /**
   * End the current authenticated session and clear all internal state
   * associated with it. If a log in attempt is currently in progress then cancel it.
   */
  logOut() {
    return this.tokenizedApi.state.dispatch(this.logOutAction());
  }

  async getLoginURL(): Promise<string> {
    // OAuth2:
    // https://datatracker.ietf.org/doc/html/rfc6749
    // PKCE (for code_challenge):
    // https://datatracker.ietf.org/doc/html/rfc7636

    if (!this.tokenizedApi.config.loginConfiguration) {
      throw new Error('No login configuration supplied');
    }

    const parameters = {
      client_id: this.tokenizedApi.config.loginConfiguration?.client,
      scope: this.tokenizedApi.config.loginConfiguration?.scope,
      redirect_uri: this.tokenizedApi.config.loginConfiguration?.redirect,
      response_type: 'code',
      code_challenge: await computeCodeChallenge(makeCodeVerifier()),
      code_challenge_method: 'S256',
    };

    return `${
      this.tokenizedApi.config.loginConfiguration?.url
    }#${new URLSearchParams(parameters)}`;
  }

  extractOauthDetails(encodedOauthDetails: string) {
    const params = new URLSearchParams(encodedOauthDetails);
    return {
      clientId: params.get('client_id'),
      scope: params.get('scope'),
      redirectURI: params.get('redirect_uri'),
      codeChallenge: params.get('code_challenge'),
    };
  }

  async getClientDetails(encodedOauthDetails: string) {
    const { clientId } = this.extractOauthDetails(encodedOauthDetails);
    const response = await this.tokenizedApi.endpoints.get<
      Record<string, unknown>
    >(urlPath('auth', 'oauth', 'client', clientId || ''));
    return {
      name: response.data.name,
      redirection: response.data.redirection,
    };
  }

  async getAuthCodeRedirect(encodedOauthDetails: string) {
    const { clientId, redirectURI, codeChallenge, scope } =
      this.extractOauthDetails(encodedOauthDetails);

    const response = await this.tokenizedApi.endpoints.post<
      Record<string, unknown>
    >(urlPath('auth', 'oauth', 'code'), {
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectURI,
      scope: scope,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    return redirectURI + '#code=' + response.data.auth_code;
  }

  async makeAuthTokens(authResponse: string): Promise<string> {
    const params = new URLSearchParams(authResponse);
    const response = await this.tokenizedApi.endpoints.post<
      Record<string, unknown>
    >(
      urlPath('auth', 'oauth', 'token'),
      {
        grant_type: 'authorization_code',
        client_id:
          params.get('client_id') ||
          this.tokenizedApi.config.loginConfiguration?.client,
        redirect_uri:
          params.get('redirect_uri') ||
          this.tokenizedApi.config.loginConfiguration?.redirect,
        code_verifier: getCodeVerifier(),
        code: params.get('code'),
      },
      undefined,
      null,
    );
    return response.data.access_token as string;
  }

  pairingCodeQuery = this.makeQuery(
    (query) => ({
      ...query,
      enabled: this.getEnableQueriesValidDuringMfa(),
      staleTime: 0,
      refetchInterval: minutes(5),
    }),
    async () => {
      const response = await this.tokenizedApi.endpoints.post<{
        data: { pairing_code: string };
      }>(
        urlPath('users', '@me', 'devices', 'pairing', 'code'),
        {},
        undefined,
        selectPreAuthToken,
      );
      return response.data.data.pairing_code;
    },
  );

  async startRecoveryPhraseBackup(
    setMfaRequest?: (mfaRequest: MFARequest | null) => void,
  ) {
    const response = await this.tokenizedApi.endpoints.post<{
      data: { handshake_id: string };
    }>(urlPath('users', '@me', 'devices', 'showSeedPhrase'), {});

    const handshakeId = response.data.data.handshake_id;

    await this.tokenizedApi.mfa.waitForMfa(handshakeId, setMfaRequest);
  }

  checkAccessTokens() {
    if (!this.tokenizedApi.config.authResponse) return;
    this.makeAuthTokens(this.tokenizedApi.config.authResponse)
      .then((accessToken) => {
        this.supplyToken(accessToken);
        this.persistAuthTokenForSessionRestore();
        return;
      })
      .catch(() => {});
  }

  /** @internal */
  onErrorFromTokenizedApi(error: TokenizedApiError) {
    if (error instanceof TokenizedAuthError) {
      // If any endpoint response has an auth error,
      // force the app into a logged out state
      this.logOut();
    }
  }

  /**
   * Returns the string that will be appended to user handles to form an account
   * identifier for log in. Display this in your log in dialog when prompting
   * the user for a handle. Since the postfix is determined by the back end
   * (`production`, `test`, or `development`), it remains fixed for the lifetime
   * of each `TokenizedApi` object.
   *
   * @returns The handle postfix, for example `'@tkz.id'`.
   */
  getUserHandlePostfix() {
    return `@${this.userHandleDomain}`;
  }

  getTokenizedBackend() {
    return selectTokenizedBackend(this.tokenizedApi.getReduxState());
  }

  checkClockSkew(response: AxiosResponse) {
    const skewSeconds =
      Math.abs(Date.parse(response.headers.date) - Date.now()) / 1e3;
    if (skewSeconds > 60) {
      throw new TokenizedClockSyncError().withFormatting(this);
    }
  }

  async askPermissionForTopic(topic: string) {
    try {
      await this.tokenizedApi.endpoints.post(
        urlPath('users', '@me', 'permission'),
        { topic },
      );
    } catch (error) {
      if (error instanceof TokenizedPermissionRestrictionError) {
        throw error;
      }
      // console.error(`Error asking permission for topic ${topic}`, error);
      return undefined;
    }

    return true;
  }
}

export default Account;
